import type { CustomerProfileUpdateInput } from '../../types'
import type { CustomerPrivacyDashboard } from '../../customerPrivacyApi'
import { formatDateTime } from '../../helpers/formatters'
import type { ShopLocale, ShopMessageKey } from '../../../i18n'
import { ConsentPreferencesCard } from './ConsentPreferencesCard'
import { PrivacyProfileCard } from './PrivacyProfileCard'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type PrivacyProfileAndConsentsSectionProps = {
  dashboard: CustomerPrivacyDashboard
  locale: ShopLocale
  consentsSaving: boolean
  onUpdateProfile: (payload: CustomerProfileUpdateInput) => Promise<boolean>
  onSaveConsents: (input: { marketingEmail: boolean; promotionalNotifications: boolean }) => void
  t: TranslateFn
}

export const PrivacyProfileAndConsentsSection = ({
  dashboard,
  locale,
  consentsSaving,
  onUpdateProfile,
  onSaveConsents,
  t,
}: PrivacyProfileAndConsentsSectionProps) => (
  <div className="grid gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
    <PrivacyProfileCard
      title={t('customer.privacy.profile.title')}
      description={t('customer.privacy.profile.description')}
      profile={dashboard.profile}
      firstNameLabel={t('customer.auth.firstName')}
      lastNameLabel={t('customer.auth.lastName')}
      emailLabel={t('customer.auth.email')}
      phoneLabel={t('customer.auth.phone')}
      residentialAddressLabel={t('customer.dashboard.residentialAddress')}
      billingAddressLabel={t('customer.dashboard.billingAddress')}
      shippingAddressLabel={t('customer.dashboard.shippingAddress')}
      saveLabel={t('customer.privacy.profile.save')}
      savingLabel={t('customer.privacy.profile.saving')}
      onSave={onUpdateProfile}
    />

    <ConsentPreferencesCard
      title={t('customer.privacy.consents.title')}
      description={t('customer.privacy.consents.description')}
      mandatoryConsentLabel={t('customer.privacy.consents.mandatory')}
      mandatoryConsentValue={
        dashboard.consents.privacyPolicyAccepted
          ? t('customer.privacy.consents.accepted')
          : t('customer.privacy.consents.notAccepted')
      }
      mandatoryConsentDateLabel={t('customer.privacy.consents.acceptedAt')}
      mandatoryConsentDateValue={formatDateTime(
        dashboard.consents.privacyPolicyAcceptedAt,
        locale,
        t('common.never'),
      )}
      marketingEmailLabel={t('customer.privacy.consents.marketingEmail')}
      promotionalNotificationsLabel={t('customer.privacy.consents.promotionalNotifications')}
      saveLabel={t('customer.privacy.consents.save')}
      saveBusyLabel={t('customer.privacy.consents.saving')}
      marketingEmail={dashboard.consents.marketingEmail}
      promotionalNotifications={dashboard.consents.promotionalNotifications}
      saving={consentsSaving}
      onSave={onSaveConsents}
    />
  </div>
)
